import {
  EBITDA_MARGIN_ALIAS,
  GROSS_MARGIN_ALIAS,
  LTM_EBITDA_ALIAS,
  LTM_REVENUE_ALIAS,
  LTM_REVENUE_GROWTH_ALIAS,
  NET_INCOME_ALIAS,
  NTM_EBITDA_ALIAS,
  NTM_REVENUE_ALIAS,
  NTM_REVENUE_GROWTH_ALIAS,
  PERFORMANCE_METRIC_ALIASES,
  TANGIBLE_BOOK_VALUE_ALIAS,
} from 'common/constants/financials';
import { DEFAULT_ZERO_VALUE } from 'common/constants/valuations';
import { FinancialsTransposed } from 'pages/ValuationsAllocation/util';
import { getNumberOrNAValue } from 'utilities';
import { parseFloatToFixed } from 'utilities/parseFloatToFixed';
import { ValueType } from './types';
import {
  MULTIPLE_PREMIUM_DISCOUNT_MEASUREMENT_DATE,
} from '../MultiplePremiumDiscount/common/constants';
import { PERFORMANCE_METRICS_SPREADSHEET_COMPANY } from '../PerformanceMetrics/common/constants/performanceMetrics/sheetAliases';
import { PERFORMANCE_METRICS_SPREADSHEET_COLUMNS } from '../PerformanceMetrics/common/constants/performanceMetrics/sheetTitles';

export const getTopNObjectsFromList = (objectList: any[], quantity: number) =>
  objectList.toSorted((object1, object2) => object2.id - object1.id).slice(0, quantity);

export const ensureNoMoreThanTwoDecimalPlaces = (value: ValueType) => {
  const parsedValue = getNumberOrNAValue(value);

  return typeof parsedValue === 'number' ? parseFloatToFixed(parsedValue.toString(), 2) : parsedValue;
};

const getSortedPremiumDiscounts = (multiplePremiumDiscount: any) => [...multiplePremiumDiscount].sort((a, b) => b.id - a.id);

export const getTotalPremiumDiscount = (multiplePremiumDiscount: any) => {
  if (!multiplePremiumDiscount || multiplePremiumDiscount.length === 0) {
    return 0;
  }
  const sortedPremiumDiscounts = getSortedPremiumDiscounts(multiplePremiumDiscount).filter(
    item => item.name === MULTIPLE_PREMIUM_DISCOUNT_MEASUREMENT_DATE
  );
  return sortedPremiumDiscounts[0].total_premium_discount;
};

export const getMultiplePremiumDiscountOptions = (mpdReference: string, multiplePremiumDiscount: any) => {
  const totalPremiumDiscount = getTotalPremiumDiscount(multiplePremiumDiscount);
  return [
    { label: DEFAULT_ZERO_VALUE, value: parseFloatToFixed('0', 3) },
    { label: parseFloatToFixed(totalPremiumDiscount ?? 0, 3).toString(), value: `=${mpdReference}.C8` },
  ];
};

export const getMultiplePremiumDiscountOptionsForCell = (mpdReference: string, totalPremiumDiscount: string | null) => [
  { label: DEFAULT_ZERO_VALUE, value: parseFloatToFixed('0', 3) },
  { label: parseFloatToFixed(totalPremiumDiscount ?? '0', 3).toString(), value: `=${mpdReference}.C8` },
];

export const getFiscalYearEnd = (value: string, companyMeasurementDate: any) => {
  const asOfDateISO = new Date(value).toISOString();
  const yearFromAsOfDate = asOfDateISO.split('-')[0];
  const { end_day: endDay, end_month: endMonth } = companyMeasurementDate.company.fiscal_year_end ?? {};
  return `${yearFromAsOfDate}-${endMonth}-${endDay}`;
};

const OFFSET_FOR_TITLE_COLUMN = 1;
const metricIndexes = {
  [LTM_REVENUE_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE - OFFSET_FOR_TITLE_COLUMN,
  [NTM_REVENUE_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE - OFFSET_FOR_TITLE_COLUMN,
  [LTM_EBITDA_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA - OFFSET_FOR_TITLE_COLUMN,
  [NTM_EBITDA_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA - OFFSET_FOR_TITLE_COLUMN,
  [LTM_REVENUE_GROWTH_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE_GROWTH - OFFSET_FOR_TITLE_COLUMN,
  [NTM_REVENUE_GROWTH_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE_GROWTH - OFFSET_FOR_TITLE_COLUMN,
  [GROSS_MARGIN_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.GROSS_MARGIN - OFFSET_FOR_TITLE_COLUMN,
  [EBITDA_MARGIN_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.EBITDA_MARGIN - OFFSET_FOR_TITLE_COLUMN,
  [TANGIBLE_BOOK_VALUE_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK - OFFSET_FOR_TITLE_COLUMN,
  [NET_INCOME_ALIAS]: PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME - OFFSET_FOR_TITLE_COLUMN,
};

const retrieveMetricValue = (metricValues: any, targetMetric: string) => metricValues[metricIndexes[targetMetric as keyof typeof metricIndexes]];

export const getCompanyPerformanceMetrics = (columns: any) => {
  if (!Array.isArray(columns)) {
    throw new TypeError('Expected columns to be an array');
  }
  const performanceMetrics = columns.map(column => {
    if (column[PERFORMANCE_METRICS_SPREADSHEET_COMPANY]) {
      return column[PERFORMANCE_METRICS_SPREADSHEET_COMPANY].value;
    }
    return null;
  });
  const metrics: Record<string, ValueType> = {};
  PERFORMANCE_METRIC_ALIASES.forEach(alias => {
    metrics[alias] = retrieveMetricValue(performanceMetrics, alias);
  });
  return metrics;
};

export const calcTangibleBookValue = (financialsParam: FinancialsTransposed) =>
  (financialsParam?.assets_total ?? 0)
  - (financialsParam?.intangibles ?? 0)
  - (financialsParam?.liabilities_total ?? 0);
